import { SystemPromptsService } from '@mpt-fo/common/core/services/system-prompts.service';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { AddAgentLocationComponent } from './../add-agent-location/add-agent-location.component';
import { PagedRequest } from '@mpt-fo/common/shared/models/paged-request.model';
import { ModalOptionsConfig, ModalService } from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import { CustomFormControl, CustomFormControlDefinition } from '@mpt-fo/common/shared/utils/custom-form-control';
import { FormBuilder, Validators } from '@angular/forms';
import { BaseFormComponent } from '@mpt-fo/common/lean-shared/components/base-form/base-form.component';
import {
	AgentLocationModel,
	AssignedSerialNoModel,
	AssignTerminalRequestModel,
	SerialRecord,
} from '@mpt-fo/common/shared/models/aggregator.model';
import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { AggregatorService } from '@mpt-fo/common/core/services/aggregator.service';
import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DeviceService } from '@mpt-fo/common/core/services/device.service';
import { LocalStorageService } from '@mpt-fo/common/core/services/local-storage.service';
import { AppConfig } from '@mpt-fo/common/shared/config/app-config';
import { BusinessService } from '@mpt-fo/common/core/services/business.service';
import { logger } from '@mpt-fo/common/telemetry';

@Component({
	selector: 'app-assign-terminal',
	templateUrl: './assign-terminal.component.html',
	styleUrls: ['./assign-terminal.component.scss'],
})
export class AssignTerminalComponent extends BaseFormComponent implements OnInit {
	@Input() agentId;
	@Input() agentData;
	@Input() selectedAgentContext;
	@Input() fixedPricing;
	errorMessage: string;
	assignedSerialNos: AssignedSerialNoModel;
	location: AgentLocationModel;
	amountControl: CustomFormControlDefinition;
	selectedSerial: SerialRecord;
	runningModal: NzModalRef;
	inputtedAmount = 0;
	activationAmount = 0;
	showFeeWaiver = false;
	showAssignTerminalUpdate = false;

	constructor(
		private aggregatorService: AggregatorService,
		public fb: FormBuilder,
		private modal: ModalService,
		private promptService: SystemPromptsService,
		private localStorage: LocalStorageService,
		private deviceService: DeviceService,
		private router: Router,
		public injectedConfig: AppConfig,
		private businessService: BusinessService
	) {
		super(fb);
		this.amountControl = new CustomFormControl()
			.id('amount')
			.placeholder('Enter BRM Fee (optional)')
			.name('Amount')
			.isCurrencyAmount()
			.build();

		this.form = fb.group({
			amount: this.amountControl.formGroupControl,
			location: [null, [Validators.required]],
			serialNo: [null, [Validators.required]],
		});
	}

	ngOnInit(): void {
		this.showAssignTerminalUpdate = this.injectedConfig.SHOW_TERMINAL_ASSIGNMENT_BUSINESS_UPDATE;
		this.getAllAgentLocation();
		this.getAssignedSerialNos();
		this.onSerialChange();
		this.onAmountChange();
		this.showFeeWaiver = this.injectedConfig.SHOW_FEE_WAIVER;
	}

	assignTerminal() {
		if (!this.validateForm()) {
			return;
		}
		this.promptService
			.openTerminalAssignmentPrompt()
			.then((consent) => {
				const params = this.buildParams();
				this.submittingForm = true;
				this.aggregatorService
					.assignTerminal(params, this.selectedAgentContext)
					.then((res) => {
						const config = new ModalOptionsConfig();
						config.nzComponentParams.alertType = 'success';
						config.nzComponentParams.message = 'Successful';
						this.modal.alert(config);
						this.submittingForm = false;
					})
					.catch((err) => {
						this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
						this.submittingForm = false;
					});
			})
			.catch((err) => {
				logger.error(err);
			});
	}

	buildParams() {
		const form = this.form.value;
		const params = new AssignTerminalRequestModel();
		params.agentId = this.agentId;
		params.agentLocationId = form.location.id;
		params.aggregatorFee = this.selectedSerial.maximumAggregatorFee;
		params.id = this.selectedSerial.id;
		return params;
	}

	onSerialChange(): void {
		this.form.get('serialNo').valueChanges.subscribe((val) => {
			this.selectedSerial = val;
			if (this.selectedSerial.maximumAggregatorFee) {
				this.amountControl.maxValue = this.selectedSerial.maximumAggregatorFee;
			}
			this.activationAmount = this.selectedSerial.activationAmount;
		});
	}

	onAmountChange(): void {
		this.form.get('amount').valueChanges.subscribe((val) => {
			this.inputtedAmount = parseFloat(val);
			if (this.selectedSerial.maximumAggregatorFee) {
				this.amountControl.maxValue = this.selectedSerial.maximumAggregatorFee;
			}
		});
	}

	getAssignedSerialNos() {
		const pageParams = new PagedRequest({ pageNumber: 1, pageSize: 1000 });
		const params = { keyword: '', ...pageParams };
		this.aggregatorService
			.getAssignedSerialNos(params, this.selectedAgentContext)
			.then((res) => {
				this.assignedSerialNos = res;
			})
			.catch((err) => {
				this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
			});
	}

	getAllAgentLocation() {
		this.aggregatorService
			.getAllAgentLocation(this.agentId)
			.then((res) => {
				this.location = res;
			})
			.catch((err) => {
				this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
			});
	}

	launchAgentLocationModal() {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.data = { agentId: this.agentId };
		config.nzComponentParams.callback = () => this.getAllAgentLocation();
		this.runningModal = this.modal.createModal(AddAgentLocationComponent, config);
	}

	requestPriceConcession() {
		this.checkBRMLevel('Fee_Concession');
	}

	createFeeWaiverRequest() {
		this.checkBRMLevel('Fee_Waiver');
	}

	checkBRMLevel(requestType: 'Fee_Concession' | 'Fee_Waiver'): any {
		this.aggregatorService
			.getBRMRank()
			.then((res) => {
			/* 	if (+res.brmRank < 3) {
					const message = 'You are unable to access this feature because of your KYC level. Please upgrade and try again.';
					this.alertModal(message);
					return false;
				} else { */
					this.checkBusinessKYCLevel(requestType);
			//	}
			})
			.catch((error) => {
				/* if (this.agentData && this.agentData.kycVerificationStatus !== 'APPROVED') {
					const message = 'You are unable to access this feature because of the business KYC Level, Upgrade and try again.';
					this.alertModal(message);
					return false;
				} else { */
					this.checkBusinessKYCLevel(requestType);
				//}
			});
	}

	checkBusinessKYCLevel(requestType: 'Fee_Concession' | 'Fee_Waiver') {
		/* if (this.agentData.kycVerificationStatus !== 'APPROVED') {
			const message = 'You are unable to access this feature because of the business KYC Level, Upgrade and try again.';
			this.alertModal(message);
			return false;
		} else { */
			if (requestType == 'Fee_Concession') {
				if (this.fixedPricing) {
					const message = 'This business already has an existing fee concession request';
					this.alertModal(message);
					return false;
				} else {
					this.aggregatorService.setPricingPlanRequestData(null);
					this.localStorage.deleteValue('priceConsession');
					this.router.navigateByUrl(`main-app/aggregator/agents/user/pricing-request/${this.agentId}`);
				}
			} else {
				this.deviceService.setFeeWaiverItem({ businessRoute: true, businessId: this.agentId });
				this.localStorage.save('feeWaiver', JSON.stringify({ businessRoute: true, businessId: this.agentId }));
				this.router.navigateByUrl(`main-app/aggregator/create-fee-waiver`);
			}
		//}
	}

	alertModal(message: string) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.alertType = 'error';
		config.nzComponentParams.title = '';
		config.nzWidth = 396;
		config.nzCentered = true;
		config.nzComponentParams.message = message;
		config.nzComponentParams.callback = (result) => {
			this.onProceed();
		};
		this.runningModal = this.modal.alert(config);
	}

	onProceed() {
		this.runningModal?.close(true);
	}

	navigateToPosInventory() {
		this.businessService.setPosRequestSelectedBusiness(this.agentData);
		this.router.navigate(['/main-app/aggregator/terminal-management'], { queryParams: { agentId: this.agentId } });
	}
}
